// Turns available report dates into a browsable calendar.
// Groups their months into quarters and years for the date-range dialog, and locates
// report periods within that calendar so selecting a range can bring its dates into view.
import { DateTime } from 'luxon';
import { PeriodOption } from '../report-settings/reportSettings.ts';
import { PeriodOptionSet } from './createPeriodOptions.ts';


export const REPORT_TIME_ZONE = 'Europe/Helsinki';
export const VISIBLE_QUARTER_COUNT = 4;

export interface Quarter {
    key: string;
    year: number;
    quarter: number;
    months: DateTime[];
}

export function buildPeriodIndex(periods: PeriodOption[]): Map<string, number> {
    // Helsinki midnight can fall on the previous UTC date. Calendar cells use Helsinki dates.
    const periodIndex = new Map(periods.map((period, index) => [
        DateTime.fromJSDate(period.start).setZone(REPORT_TIME_ZONE).toISODate()!,
        index,
    ]));
    return periodIndex;
}

export function buildCalendar(periodOptionSet: PeriodOptionSet): { quarters: Quarter[]; years: number[] } {
    // The first and last available daily report periods set the calendar's limits.
    // Using those same limits for Weeks and Months avoids changing which months can
    // be browsed when the user switches the calendar's selection mode.
    const firstDate = DateTime.fromJSDate(periodOptionSet.days[0].start)
        .setZone(REPORT_TIME_ZONE).startOf('month');
    const lastDate = DateTime.fromJSDate(periodOptionSet.days.at(-1)!.start)
        .setZone(REPORT_TIME_ZONE).startOf('month');
    const months: DateTime[] = [];
    for (let month = firstDate; month <= lastDate; month = month.plus({ months: 1 })) {
        months.push(month);
    }

    const quarters: Quarter[] = [];
    for (const month of months) {
        const quarter = Math.ceil(month.month / 3);
        const key = `${month.year}-${quarter}`;
        const existing = quarters.at(-1);
        if (existing?.key === key) existing.months.push(month);
        else quarters.push({
            key,
            year: month.year,
            quarter,
            months: [month],
        });
    }

    return {
        quarters,
        years: [...new Set(quarters.map((quarter) => quarter.year))],
    };
}

export function findQuarterIndex(quarters: Quarter[], period: PeriodOption | undefined): number {
    const lastQuarterIndex = quarters.length - 1;
    if (!period) return lastQuarterIndex;
    const date = DateTime.fromJSDate(period.start).setZone(REPORT_TIME_ZONE);
    const quarter = Math.ceil(date.month / 3);
    const index = quarters.findIndex(
        (calendarQuarter) => calendarQuarter.year === date.year && calendarQuarter.quarter === quarter,
    );
    const quarterIndex = index === -1 ? lastQuarterIndex : index;
    return quarterIndex;
}

export function getQuarterWindowStart(targetIndex: number, quarterCount: number): number {
    // Show the quarter containing the chosen report date at the right of the calendar,
    // with the three preceding quarters to its left. Stop at the available history limits.
    const maximumStart = Math.max(0, quarterCount - VISIBLE_QUARTER_COUNT);
    const windowStart = Math.max(0, Math.min(targetIndex - VISIBLE_QUARTER_COUNT + 1, maximumStart));
    return windowStart;
}
